/**
 * Rabais (design 4.3) : en % ou en $, sur l'article selectionne ou sur toute
 * la vente. La decision (droit, plafond, montant) est dans vente/droits.js ;
 * ce dialogue ne fait que la saisie. `onAppliquer` recoit
 * { portee, type, valeur } et renvoie un message d'erreur ou null.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import PaveSaisie from './PaveSaisie';
import { PORTEES_RABAIS, TYPES_RABAIS } from './contrat';
import { centsDepuisSaisie, pointsDeBaseDepuisSaisie } from '../domain/money';
import { palette } from '../app/theme';

const BASCULE_SX = { minHeight: 64, fontSize: 18, fontWeight: 700, textTransform: 'none', flex: 1 };

/** Limite de l'employe, en texte. */
export function texteLimite(droits) {
    const pct = droits ? droits.max_discount_pct : null;
    if (pct === null || pct === undefined) return 'Votre limite : sans limite';
    return `Votre limite : ${String(pct).replace('.', ',')} %`;
}

export default function DialogueRabais({
    open, droits, ligne, venteAUnRabais, ligneAUnRabais, onAppliquer, onRetirer, onAnnuler,
}) {
    // Etat neuf a chaque ouverture : l'ecran de vente change la `key` de ce
    // dialogue a chaque ouverture (aucun effet de remise a zero, qui pourrait
    // passer APRES les premieres touches tapees).
    const [portee, setPortee] = useState(ligne && !venteAUnRabais ? PORTEES_RABAIS.LIGNE : PORTEES_RABAIS.VENTE);
    const [type, setType] = useState(TYPES_RABAIS.POURCENT);
    const [valeur, setValeur] = useState('');
    const [erreur, setErreur] = useState(null);

    const appliquer = () => {
        const nombre = type === TYPES_RABAIS.POURCENT ? pointsDeBaseDepuisSaisie(valeur) : centsDepuisSaisie(valeur);
        if (nombre === null || nombre <= 0) {
            setErreur(type === TYPES_RABAIS.POURCENT ? 'Entrez un pourcentage de 0,01 à 100.' : 'Entrez un montant plus grand que 0.');
            return;
        }
        setErreur(onAppliquer({ portee, type, valeur: nombre }));
    };

    const existant = portee === PORTEES_RABAIS.VENTE ? venteAUnRabais : ligneAUnRabais;

    return (
        <Dialog open={open} onClose={onAnnuler} disableRestoreFocus aria-labelledby="dialogue-rabais-titre" data-testid="dialogue-rabais">
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2, width: 440 }}>
                <Typography id="dialogue-rabais-titre" component="h2" sx={{ fontSize: 28, fontWeight: 800, color: palette.text }}>
                    Rabais
                </Typography>
                <ToggleButtonGroup exclusive value={portee} onChange={(_e, v) => v && setPortee(v)} aria-label="Rabais sur">
                    <ToggleButton value={PORTEES_RABAIS.LIGNE} disabled={!ligne || venteAUnRabais} sx={BASCULE_SX}>
                        {ligne ? `Article : ${ligne.name}` : 'Article'}
                    </ToggleButton>
                    <ToggleButton value={PORTEES_RABAIS.VENTE} sx={BASCULE_SX}>Toute la vente</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup exclusive value={type} onChange={(_e, v) => { if (v) { setType(v); setValeur(''); } }} aria-label="Type de rabais">
                    <ToggleButton value={TYPES_RABAIS.POURCENT} sx={BASCULE_SX}>En %</ToggleButton>
                    <ToggleButton value={TYPES_RABAIS.MONTANT} sx={BASCULE_SX}>En $</ToggleButton>
                </ToggleButtonGroup>
                <Box
                    data-testid="dialogue-rabais-valeur"
                    aria-live="polite"
                    sx={{
                        height: 64, border: `3px solid ${palette.primary}`, borderRadius: 2, display: 'flex', alignItems: 'center',
                        justifyContent: 'flex-end', px: 2, fontSize: 32, fontWeight: 800, color: palette.text,
                    }}
                >
                    {valeur || '0'}{' '}{type === TYPES_RABAIS.POURCENT ? '%' : '$'}
                </Box>
                <Typography data-testid="limite-rabais" sx={{ fontSize: 16, color: palette.textSecondary }}>{texteLimite(droits)}</Typography>
                <Box role="alert" data-testid="dialogue-rabais-message" sx={{ minHeight: 24 }}>
                    {erreur && <Typography sx={{ fontSize: 16, fontWeight: 700, color: palette.errorOnTint }}>{erreur}</Typography>}
                </Box>
                <Box sx={{ alignSelf: 'center' }}>
                    <PaveSaisie valeur={valeur} onChange={setValeur} onValider={appliquer} decimale longueurMax={9} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button variant="outlined" onClick={onAnnuler} sx={{ flex: 1 }}>Annuler</Button>
                    <Button variant="contained" onClick={appliquer} sx={{ flex: 1 }}>Appliquer</Button>
                </Box>
                {existant && (
                    <Button variant="text" color="error" onClick={() => onRetirer(portee)} sx={{ minHeight: 48 }}>
                        Retirer le rabais
                    </Button>
                )}
            </Box>
        </Dialog>
    );
}
